import { Mail, MessageSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { INotificationStatusPopoverProps, TNotificationDeliveryStatus } from '@/@types/insurance';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function iconColor(status: TNotificationDeliveryStatus): string {
  if (status === 'sent') return 'text-green-500';
  if (status === 'error') return 'text-destructive';
  return 'text-muted-foreground';
}

function deriveEmailStatus(
  tracking: INotificationStatusPopoverProps['tracking']
): TNotificationDeliveryStatus {
  if (!tracking) return 'pending';
  if (tracking.emailError) return 'error';
  if (tracking.emailSentAt) return 'sent';
  return 'pending';
}

function deriveSmsStatus(
  tracking: INotificationStatusPopoverProps['tracking']
): TNotificationDeliveryStatus {
  if (!tracking) return 'pending';
  if (tracking.smsError) return 'error';
  if (tracking.smsSentAt) return 'sent';
  return 'pending';
}

function deriveCompanyEmailStatus(
  tracking: INotificationStatusPopoverProps['tracking']
): TNotificationDeliveryStatus {
  if (!tracking) return 'pending';
  if (tracking.companyEmailError) return 'error';
  if (tracking.companyEmailSentAt) return 'sent';
  return 'pending';
}

function StatusRow({
  label,
  status,
  sentAt,
  error,
}: {
  label: string;
  status: TNotificationDeliveryStatus;
  sentAt?: number;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-4">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span
          className={cn(
            'text-xs capitalize',
            status === 'sent' && 'text-green-500',
            status === 'error' && 'text-destructive',
            status === 'pending' && 'text-muted-foreground'
          )}
        >
          {status}
        </span>
      </div>
      {sentAt && (
        <span className="text-xs text-muted-foreground">{formatDate(sentAt)}</span>
      )}
      {error && (
        <span className="text-xs text-destructive truncate max-w-[200px]" title={error}>
          {error}
        </span>
      )}
    </div>
  );
}

export function NotificationStatusPopover({ tracking }: INotificationStatusPopoverProps) {
  const emailStatus = deriveEmailStatus(tracking);
  const smsStatus = deriveSmsStatus(tracking);
  const companyEmailStatus = deriveCompanyEmailStatus(tracking);

  const hasAnyActivity = tracking !== null && (
    tracking.emailSentAt !== undefined ||
    tracking.smsSentAt !== undefined ||
    tracking.companyEmailSentAt !== undefined ||
    tracking.emailError !== undefined ||
    tracking.smsError !== undefined ||
    tracking.companyEmailError !== undefined
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          aria-label="View notification delivery status"
          onClick={e => e.stopPropagation()}
        >
          <Mail
            className={cn(
              'h-4 w-4',
              !hasAnyActivity ? 'text-muted-foreground/40' : iconColor(emailStatus)
            )}
          />
          <MessageSquare
            className={cn(
              'h-4 w-4',
              !hasAnyActivity ? 'text-muted-foreground/40' : iconColor(smsStatus)
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end" onClick={e => e.stopPropagation()}>
        <p className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wider">
          Notification Status
        </p>
        {!tracking ? (
          <p className="text-xs text-muted-foreground">No notifications sent yet. The daily cron will process this policy automatically.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <StatusRow
              label="Email"
              status={emailStatus}
              sentAt={tracking.emailSentAt}
              error={tracking.emailError}
            />
            <StatusRow
              label="SMS"
              status={smsStatus}
              sentAt={tracking.smsSentAt}
              error={tracking.smsError}
            />
            <StatusRow
              label="Company Email"
              status={companyEmailStatus}
              sentAt={tracking.companyEmailSentAt}
              error={tracking.companyEmailError}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
